import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockArticles } from '../../mocks/blog';
import type { Article } from '../../mocks/blog';
import AdminLogin from './components/AdminLogin';
import ArticleList from './components/ArticleList';
import ArticleForm from './components/ArticleForm';
import PageSeo from '@/components/feature/PageSeo';

type View = 'list' | 'new' | 'edit';

function loadArticles(): Article[] {
  try {
    const stored = localStorage.getItem('blog_articles');
    if (stored) {
      const parsed: Article[] = JSON.parse(stored);
      if (parsed.length > 0) return parsed;
    }
  } catch {
    // ignore
  }
  return mockArticles;
}

function saveArticles(articles: Article[]) {
  localStorage.setItem('blog_articles', JSON.stringify(articles));
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [view, setView] = useState<View>('list');
  const [articles, setArticles] = useState<Article[]>([]);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (sessionStorage.getItem('admin_auth') === '1') {
      setAuthed(true);
    }
  }, []);

  useEffect(() => {
    if (authed) {
      setArticles(loadArticles());
    }
  }, [authed]);

  const showToast = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const handleLogin = () => setAuthed(true);

  const handleLogout = () => {
    sessionStorage.removeItem('admin_auth');
    setAuthed(false);
  };

  const handleNew = () => {
    setEditingArticle(null);
    setView('new');
  };

  const handleEdit = (article: Article) => {
    setEditingArticle(article);
    setView('edit');
  };

  const handleSave = (article: Article) => {
    let updated: Article[];
    if (view === 'edit') {
      updated = articles.map((a) => (a.id === article.id ? article : a));
      showToast('文章已更新！');
    } else {
      updated = [article, ...articles];
      showToast('文章已儲存！');
    }
    saveArticles(updated);
    setArticles(updated);
    setView('list');
    setEditingArticle(null);
  };

  const handleCancelForm = () => {
    setView('list');
    setEditingArticle(null);
  };

  const handleDeleteRequest = (id: string) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirmId) return;
    const updated = articles.filter((a) => a.id !== deleteConfirmId);
    saveArticles(updated);
    setArticles(updated);
    setDeleteConfirmId(null);
    showToast('文章已刪除');
  };

  const handleToggleStatus = (id: string) => {
    const updated = articles.map((a) =>
      a.id === id
        ? { ...a, status: a.status === 'published' ? ('draft' as const) : ('published' as const) }
        : a
    );
    saveArticles(updated);
    setArticles(updated);
    const target = updated.find((a) => a.id === id);
    showToast(target?.status === 'published' ? '已發布文章' : '已設為草稿');
  };

  if (!authed) {
    return (
      <>
        <PageSeo title="後台管理 - 四相AI影視學院" noIndex={true} />
        <AdminLogin onLogin={handleLogin} />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <PageSeo title="後台管理 - 四相AI影視學院" noIndex={true} />
      {/* Top nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-lg border-b border-amber-500/10">
        <div className="w-full px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-sm bg-amber-500/15 border border-amber-500/25">
                <i className="ri-settings-3-line text-amber-400 text-sm" />
              </div>
              <div>
                <span
                  className="text-white/80 text-sm font-semibold tracking-wider"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  後台管理
                </span>
                <span className="text-white/25 text-xs tracking-[0.2em] ml-2" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>
                  ADMIN
                </span>
              </div>
            </div>

            <div className="w-px h-4 bg-white/10" />

            <button
              onClick={() => navigate('/blog')}
              className="flex items-center gap-1.5 text-white/30 hover:text-amber-400 transition-colors duration-300 text-xs tracking-wider cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
            >
              <i className="ri-external-link-line text-sm" />
              查看文章頁
            </button>

            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-1.5 text-white/30 hover:text-amber-400 transition-colors duration-300 text-xs tracking-wider cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
            >
              <i className="ri-home-line text-sm" />
              回到首頁
            </button>
          </div>

          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 border border-white/10 rounded-sm text-white/30 hover:border-red-400/30 hover:text-red-400 transition-all duration-300 text-xs tracking-wider cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            <i className="ri-logout-box-r-line text-sm" />
            登出
          </button>
        </div>
      </nav>

      {/* Main content */}
      <main className="pt-20 px-8 pb-16">
        <div className="max-w-5xl mx-auto">
          {/* Page title */}
          <div className="py-10">
            <div className="flex items-center gap-4 mb-2">
              <div className="w-8 h-px bg-gradient-to-r from-amber-500/60 to-transparent" />
              <span
                className="text-amber-500/50 text-xs tracking-[0.4em] uppercase"
                style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
              >
                Content Management
              </span>
            </div>
            <h1
              className="text-3xl font-black tracking-wider"
              style={{
                fontFamily: "'Noto Serif TC', serif",
                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 40%, #fde68a 70%, #d97706 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              文章管理
            </h1>
          </div>

          <ArticleList
            articles={articles}
            onEdit={handleEdit}
            onDelete={handleDeleteRequest}
            onToggleStatus={handleToggleStatus}
            onNew={handleNew}
          />
        </div>
      </main>

      {/* Article form overlay */}
      {(view === 'new' || view === 'edit') && (
        <ArticleForm
          article={editingArticle}
          onSave={handleSave}
          onCancel={handleCancelForm}
        />
      )}

      {/* Delete confirm modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
          <div className="relative w-full max-w-sm bg-[#0d0d0d] border border-amber-500/20 rounded-sm p-8">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-500/40 to-transparent" />
            <div className="flex justify-center mb-5">
              <div className="w-12 h-12 flex items-center justify-center rounded-sm bg-red-500/10 border border-red-500/20">
                <i className="ri-delete-bin-2-line text-xl text-red-400" />
              </div>
            </div>
            <h3
              className="text-center text-white/80 text-base font-semibold mb-2 tracking-wider"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              確認刪除文章？
            </h3>
            <p
              className="text-center text-white/30 text-xs leading-relaxed mb-7"
              style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
            >
              此操作無法復原，文章將永久刪除。
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-2.5 border border-white/10 rounded-sm text-white/40 text-xs tracking-wider hover:border-white/20 hover:text-white/60 transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
              >
                取消
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="flex-1 py-2.5 bg-red-500/80 hover:bg-red-500 rounded-sm text-white text-xs font-bold tracking-wider transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
              >
                確認刪除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-3 px-6 py-3 rounded-sm border shadow-2xl transition-all duration-300 ${
            toast.type === 'success'
              ? 'bg-emerald-500/15 border-emerald-400/30 text-emerald-400'
              : 'bg-red-500/15 border-red-400/30 text-red-400'
          }`}
          style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
        >
          <i className={`text-base ${toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}`} />
          <span className="text-sm tracking-wider whitespace-nowrap">{toast.msg}</span>
        </div>
      )}
    </div>
  );
}
